import { useEffect, useState } from "react";
import { LuArrowLeft } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import Preloader from "../../app-components/preloader/Preloader";
import { SITELINKS } from "../../app-routes/links";
import { OptionProps } from "../../interface-and-types";
import {
  ActionTrackerInterface,
  ActionTrackerSchema,
} from "../../interface-and-types/inspection.schema";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { notifySuccess } from "../../app-components/toast/toast";

const VOCreateActionTracker = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [vessels, setVessels] = useState<OptionProps[]>([]);
  const [form, setForm] = useState<Partial<ActionTrackerInterface>>({});
  const [criticalEquipmentList, setCriticalEquipmentList] = useState<any[]>([]);
  const [defectType, setDefectType] = useState<any[]>([]);
  const [marineSuperintendent, setMarineSuperintendent] = useState<any[]>([]);
  const [priority, setPriority] = useState<any[]>([]);
  const [serverError, setServerError] = useState<string>("");
  const [errors, setErrors] = useState<
    Partial<Record<keyof ActionTrackerInterface, string>>
  >({});
  const [equipmentList, setEquipmentList] = useState<any[]>([]);

  useEffect(() => {
    getVessels();
    getCriticalEquipmentTypes();
    getDefectType();
    getMarineSuperintendent();
    getPriorities();
    getEquipmentList();
  }, []);

  const handleInput = (e: any) => {
    setForm((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleDateInput = (input: string, date: any) => {
    setForm((prev: any) => ({ ...prev, [input]: date.toISOString() }));
  };

  const getVessels = async () => {
    try {
      const res: any = await apiClient.get("Vessels/VesselOperator");
      if (res && res.data) {
        const options: OptionProps[] = res.data.data.map(
          (i: any, index: number) => ({
            label: i.name,
            value: i.id,
            name: "Vessels",
            key: index,
          })
        );
        setVessels(options);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  const getCriticalEquipmentTypes = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/CriticalEquipmentTypes");
      setCriticalEquipmentList(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDefectType = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("DefectType");
      setDefectType(res.data.data.filter((item: any) => !item.isDeleted));
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getMarineSuperintendent = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(
        "users?Rolename=MarineSuperintendent&pageSize=1000"
      );
      setMarineSuperintendent(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorities = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/Priorities");
      setPriority(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    const formData = { ...form };

    if (files) {
      const filesArray = Array.from(files);

      const base64Promises = filesArray.map(async (file) => {
        const base64 = await fileToBase64(file);
        return {
          base64Image: base64,
          mimeType: file.type,
        };
      });

      // Convert all files to Base64
      Promise.all(base64Promises).then((base64Arr: any) => {
        formData.defectCheckListAttachments = base64Arr;
        setForm(formData);
      });
    }
  };

  const handleCreate = async (e: any) => {
    e.preventDefault();
    setErrors({});
    setServerError("");

    const result = ActionTrackerSchema.safeParse(form);

    if (result.success) {
      setLoading(true);
      try {
        const data = result.data;
        await apiClient.post("ActionTracker/VesselOperator/Create", data);

        notifySuccess("Created successfully!");
        navigate(SITELINKS.VO_TRACKER);
      } catch (error: any) {
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<
        Record<keyof ActionTrackerInterface, string>
      > = {};

      result.error.errors.forEach((err: any) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof ActionTrackerInterface;
          formattedErrors[key] = err.message;
        }
      });

      setErrors(formattedErrors);

      setServerError(
        "Form validation error occured! Kindly complete all required fields."
      );
    }
  };

  const getEquipmentList = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Equipment");
      setEquipmentList(res.data.data);
    } catch (error) {
      console.error("Error fetching", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-6 pb-24 flex justify-between items-center">
          <h4 className="lg:text-[16px] text-white nunito font-medium flex gap-2">
            <Link to={SITELINKS.VO_TRACKER} className="text-2xl">
              <LuArrowLeft />
            </Link>
            <span>Add New Action Tracker</span>
          </h4>
        </div>
      </section>

      {/* page content */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-90px]">
        <div className="rounded-lg pb-5 border border-gray-200 flex gap-6">
          <div className="w-full p-6 rounded-lg shadow bg-white">
            <form className="w-full" onSubmit={handleCreate}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 w-full">
                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Vessel Name
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <select
                    name="vesselId"
                    value={form?.vesselId}
                    className="w-full p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    onChange={handleInput}
                  >
                    <option value={""}>Select</option>
                    {vessels.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.vesselId}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Equipment Name
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <select
                    name="equipmentId"
                    value={form?.equipmentId}
                    className="w-full p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    onChange={handleInput}
                  >
                    <option value={""}>Select</option>
                    {equipmentList.map((item) => (
                      <option key={item?.id} value={item?.id}>
                        {item?.name}
                      </option>
                    ))}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.equipmentId}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Critical Equipment
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <select
                    name="criticalEquipment"
                    className="w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    value={form?.criticalEquipment}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    {criticalEquipmentList.map((item, i) => {
                      return (
                        <option key={i} value={item?.value}>
                          {item?.name}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.criticalEquipment}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Type Of Defect
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <select
                    name="defectTypeId"
                    className="w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    value={form?.defectTypeId}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    {defectType.map((item, i) => {
                      return (
                        <option key={i} value={item?.id}>
                          {item?.name}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.defectTypeId}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Select Marine Superintendent
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <select
                    name="marineSuperintendentId"
                    className="w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    value={form?.marineSuperintendentId}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    {marineSuperintendent.map((item, i) => {
                      return (
                        <option key={i} value={item?.id}>
                          {item?.firstName} {item?.lastName}
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.marineSuperintendentId}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Priority
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <select
                    name="priority"
                    className="w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    value={form?.priority}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    {priority.map((item, i) => {
                      return (
                        <option key={i} value={item?.value}>
                          {item?.name} ({item?.value})
                        </option>
                      );
                    })}
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.priority}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Responsible Party
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <input
                    name="responsibleParty"
                    value={form?.responsibleParty}
                    type="text"
                    placeholder=""
                    onChange={handleInput}
                    className="w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                  />
                  <span className="text-red-600 text-[10px]">
                    {errors.responsibleParty}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Resources Needed
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <select
                    name="resourcesNeeded"
                    className="disabled:bg-gray-100 w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    value={form?.resourcesNeeded}
                    onChange={handleInput}
                  >
                    <option value="">Select</option>
                    <option value="Human">Human</option>
                    <option value="Material">Material</option>
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.resourcesNeeded}
                  </span>
                </div>

                <div className="col-span-2">
                  <label className="text-gray-500 text-[10px] font-medium">
                    Write a comment
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <textarea
                    name="comments"
                    value={form?.comments}
                    placeholder=""
                    onChange={handleInput}
                    className="h-[60px] w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                  ></textarea>
                  <span className="text-red-600 text-[10px]">
                    {errors.comments}
                  </span>
                </div>

                <div className="col-span-2">
                  <label className="text-gray-500 text-[10px] font-medium">
                    Description of Defect
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <textarea
                    name="descriptionOfDefect"
                    value={form?.descriptionOfDefect}
                    placeholder=""
                    onChange={handleInput}
                    className="h-[60px] w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                  ></textarea>
                  <span className="text-red-600 text-[10px]">
                    {errors.descriptionOfDefect}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Action Close-Out Rate (%)
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>

                  <select
                    name="percentageOfActionClosedOut"
                    value={form?.percentageOfActionClosedOut}
                    className="w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    onChange={handleInput}
                  >
                    <option value={""}>Select</option>
                    <option value={0}>Not Started (0%)</option>
                    <option value={30}>Between 1% - 30%</option>
                    <option value={60}>Between 31% - 60%</option>
                    <option value={90}>Between 61% - 90%</option>
                    <option value={100}>Completed (100%)</option>
                  </select>
                  <span className="text-red-600 text-[10px]">
                    {errors.percentageOfActionClosedOut}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Target Close-out Date
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>

                  <div className="">
                    <DatePicker
                      id="targetCloseOutDate"
                      name="targetCloseOutDate"
                      selected={
                        form.targetCloseOutDate
                          ? new Date(form.targetCloseOutDate)
                          : null
                      }
                      onChange={(date) =>
                        handleDateInput("targetCloseOutDate", date)
                      }
                      minDate={new Date()}
                      autoComplete="off"
                      dateFormat="dd-MM-yyyy"
                      showYearDropdown
                      showMonthDropdown
                      dropdownMode="select"
                      placeholderText=""
                      className="w-full py-2 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                    />
                  </div>

                  <span className="text-red-600 text-[10px]">
                    {errors.targetCloseOutDate}
                  </span>
                </div>

                <div>
                  <label className="text-gray-500 text-[10px] font-medium">
                    Add Pictures
                    <span className="text-red-600 text-[10px]">(*)</span>
                  </label>
                  <input
                    multiple
                    type="file"
                    id="file-upload"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e)}
                    className="w-full text-xs py-1.5 p-2 border rounded focus:outline-none focus:ring focus:ring-blue-500 text-[12px]"
                  />
                  <span className="text-red-600 text-[10px]">
                    {errors.defectCheckListAttachments}
                  </span>
                </div>
              </div>

              <div className="mt-4 mb-4">
                {form.defectCheckListAttachments && (
                  <>
                    <p className="flex-col text-[11px] text-gray-500 mb-3 font-medium border-b">
                      Attached Images Preview
                    </p>
                    <div className="flex flex-wrap gap-4 sm:grid-cols-2 w-full">
                      {form.defectCheckListAttachments.map((img, index) => (
                        <div className="relative" key={index}>
                          <img
                            src={img.base64Image}
                            className="h-[50px] rounded-lg border bg-gray-100"
                            alt="base64-attached"
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="text-red-600 text-[10px] mt-8">{serverError}</div>

              <div className="mt-3">
                <button
                  type="submit"
                  className="flex gap-2 items-center whitespace-nowrap bg-red-600 shadow hover:shadow-xl p-3 rounded-lg text-white px-4 w-1/4 justify-center text-xs"
                >
                  Submit Action Tracker
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <Preloader loading={loading} />
    </>
  );
};

export default VOCreateActionTracker;
